import { cn } from '@/lib/utils'
import type { Display } from '@/types/api'
import { deriveDisplayPill, TONE_CLASSES, type StatusTone } from '../lib/status'

/**
 * Small status dot. Pass a `display` to derive the tone (lifecycle + connectivity + health
 * overlay), or an explicit `tone`. Online/healthy dots pulse softly.
 */
export function HealthDot({
  display,
  tone: toneProp,
  pulse,
  size = 'default',
  className,
  label,
}: {
  display?: Pick<Display, 'status' | 'connectivity' | 'health'>
  tone?: StatusTone
  pulse?: boolean
  size?: 'sm' | 'default' | 'lg'
  className?: string
  label?: string
}) {
  const pill = display ? deriveDisplayPill(display) : null
  const tone = toneProp ?? pill?.effectiveTone ?? 'muted'
  const shouldPulse = pulse ?? (tone === 'success' || tone === 'warning' || tone === 'danger')
  const sizeClass = size === 'sm' ? 'size-1.5' : size === 'lg' ? 'size-2.5' : 'size-2'
  return (
    <span className={cn('relative inline-flex shrink-0', sizeClass, className)} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      {shouldPulse && <span className={cn('absolute inline-flex size-full animate-ping rounded-full opacity-50 [animation-duration:2s]', TONE_CLASSES[tone].dot)} />}
      <span className={cn('relative inline-flex size-full rounded-full', TONE_CLASSES[tone].dot, tone === 'muted' && 'ring-1 ring-muted-foreground/30')} />
    </span>
  )
}
